import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ExternalLink } from "react-feather";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  ListGroup,
  Modal,
  Row,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import {
  getMeetingsAPI,
  updateMeetingAPI,
  cancelMeetingAPI,
} from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import DrawerPanel from "../../../Components/shared/DrawerPanel";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./TodaysSchedule.module.scss";

const DAY_START = 8;
const DAY_END = 20;
const PX_PER_MIN = 1.5;

function minutesFromDayStart(iso) {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes() - DAY_START * 60;
}

function blockStyle(m) {
  const start = new Date(m.startAt);
  const end = new Date(m.endAt);
  const durMin = Math.max(15, (end - start) / 60000);
  const top = minutesFromDayStart(m.startAt) * PX_PER_MIN;
  const height = durMin * PX_PER_MIN;
  return { top, height: Math.max(height, 28) };
}

function blockClass(m) {
  if (m.status === "cancelled") return styles.blockCancelled;
  if (m.status === "in-progress") return styles.blockProgress;
  if (m.displayStatus === "past" || m.status === "past")
    return styles.blockPast;
  return styles.blockScheduled;
}

export default function TodaysSchedule() {
  const [view, setView] = useState("day");
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [resOpen, setResOpen] = useState(false);
  const [newStart, setNewStart] = useState(new Date());
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMeetingsAPI();
      setMeetings(res.data || []);
      setHasLoaded(true);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = Date.now();
  const todayStr = new Date().toDateString();

  const todayMeetings = useMemo(
    () =>
      meetings.filter((m) => new Date(m.startAt).toDateString() === todayStr),
    [meetings],
  );

  const { pastMeetings, futureMeetings } = useMemo(() => {
    const past = [];
    const fut = [];
    todayMeetings.forEach((m) => {
      const ended =
        new Date(m.endAt).getTime() < now && m.status !== "cancelled";
      if (ended) past.push({ ...m, displayStatus: "past" });
      else fut.push(m);
    });
    return { pastMeetings: past, futureMeetings: fut };
  }, [todayMeetings, now]);

  const visibleMeetings = showPast
    ? [...pastMeetings, ...futureMeetings]
    : futureMeetings;

  const totalMinutes = (DAY_END - DAY_START) * 60;
  const timelineHeight = totalMinutes * PX_PER_MIN;

  const openReschedule = () => {
    if (drawer?.startAt) setNewStart(new Date(drawer.startAt));
    setResOpen(true);
  };

  const confirmReschedule = async () => {
    if (!drawer) return;
    try {
      await updateMeetingAPI(drawer._id, { startAt: newStart.toISOString() });
      toast.success("Meeting rescheduled");
      setResOpen(false);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const confirmCancel = async () => {
    if (!drawer) return;
    try {
      await cancelMeetingAPI(drawer._id);
      toast.success("Meeting cancelled");
      setCancelOpen(false);
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Today's schedule"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Dashboard", path: companyUrl("dashboard") },
            { label: "Schedule" },
          ]}
        />
        <LoadingSkeleton rows={8} cols={2} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader
          title="Today's schedule"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Dashboard", path: companyUrl("dashboard") },
            { label: "Schedule" },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  if (!loading && todayMeetings.length === 0) {
    return (
      <div>
        <PageHeader
          title="Today's schedule"
          breadcrumbs={[
            { label: "Company", path: companyUrl("dashboard") },
            { label: "Dashboard", path: companyUrl("dashboard") },
            { label: "Schedule" },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="No meetings"
          description="Nothing scheduled for today."
          actionLabel="Refresh"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Today's schedule"
        breadcrumbs={[
          { label: "Company", path: companyUrl("dashboard") },
          { label: "Dashboard", path: companyUrl("dashboard") },
          { label: "Schedule" },
        ]}
        secondaryActions={[
          { label: "Refresh", onClick: load, variant: "outline-secondary" },
        ]}
      />

      <div className="mb-3">
        <ButtonGroup>
          <Button
            size="sm"
            variant={view === "day" ? "primary" : "outline-primary"}
            onClick={() => setView("day")}
          >
            Day view
          </Button>
          <Button
            size="sm"
            variant={view === "week" ? "primary" : "outline-primary"}
            onClick={() => setView("week")}
          >
            Week view
          </Button>
        </ButtonGroup>
        {pastMeetings.length > 0 && (
          <Button
            size="sm"
            variant="link"
            className="ms-2"
            onClick={() => setShowPast((v) => !v)}
          >
            {showPast ? "Hide past" : `Show past (${pastMeetings.length})`}
          </Button>
        )}
      </div>

      <Row className="g-3">
        <Col lg={8}>
          {view === "day" ? (
            <div
              className={styles.timelineWrap}
              style={{ height: timelineHeight }}
            >
              {Array.from({ length: DAY_END - DAY_START + 1 }, (_, h) => (
                <div
                  key={h}
                  className={styles.hourRow}
                  style={{ top: h * 60 * PX_PER_MIN }}
                >
                  {String(DAY_START + h).padStart(2, "0")}:00
                </div>
              ))}
              {visibleMeetings.map((m) => {
                const st = blockStyle(m);
                return (
                  <button
                    key={m._id}
                    type="button"
                    className={`${styles.block} ${blockClass(m)}`}
                    style={{ top: st.top, height: st.height }}
                    onClick={() => setDrawer(m)}
                  >
                    <div className="fw-medium text-truncate">{m.subject}</div>
                    <div className="small text-muted text-truncate">
                      {new Date(m.startAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      · {m.organizer?.name} · {m.attendees?.length ?? 0}{" "}
                      attendees
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={styles.weekGrid}>
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
                <div key={d} className={styles.weekDay}>
                  <div className="fw-semibold mb-2">{d}</div>
                  {meetings
                    .filter((_, idx) => idx % 7 === i)
                    .map((m) => (
                      <button
                        key={m._id}
                        type="button"
                        className="mb-1 p-1 border rounded small w-100 text-start bg-transparent"
                        onClick={() => setDrawer(m)}
                      >
                        {m.subject}
                      </button>
                    ))}
                </div>
              ))}
            </div>
          )}
        </Col>
        <Col lg={4}>
          <Card className={`mb-3 ${styles.sidebarCard}`}>
            <Card.Header className="py-2">Channels in use today</Card.Header>
            <ListGroup variant="flush">
              {[...new Set(todayMeetings.map((m) => m.channel))].map((ch) => (
                <ListGroup.Item key={ch}>
                  {ch}: {todayMeetings.filter((m) => m.channel === ch).length}{" "}
                  meeting(s)
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
          <Card className={`mb-3 ${styles.sidebarCard}`}>
            <Card.Header className="py-2">Organizers today</Card.Header>
            <ListGroup variant="flush">
              {[
                ...new Map(
                  todayMeetings.map((m) => [m.organizer?.email, m.organizer]),
                ).values(),
              ].map((org) => (
                <ListGroup.Item key={org?.email}>
                  <div>{org?.name}</div>
                  <small className="text-muted">{org?.email}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
          <Card className={styles.sidebarCard}>
            <Card.Header className="py-2">Upcoming today</Card.Header>
            <ListGroup variant="flush">
              {futureMeetings.map((m) => (
                <ListGroup.Item key={m._id}>
                  {m.subject}
                  <Badge bg="info" text="dark" className="ms-2">
                    {new Date(m.startAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Card>
        </Col>
      </Row>

      <DrawerPanel
        isOpen={Boolean(drawer)}
        onClose={() => setDrawer(null)}
        title={drawer?.subject || "Meeting"}
      >
        {drawer && (
          <>
            <p>
              <Badge bg="secondary">{drawer.status}</Badge>{" "}
              <span className="text-muted">{drawer.provider}</span>
            </p>
            <p className="small mb-1">
              <strong>Organizer:</strong> {drawer.organizer?.name}
            </p>
            <p className="small mb-1">
              <strong>Attendees:</strong> {drawer.attendees?.length ?? 0}
            </p>
            {drawer.joinUrl && (
              <Button
                size="sm"
                className="mb-3"
                href={drawer.joinUrl}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink size={14} className="me-1" />
                Join link
              </Button>
            )}
            {/* <div className="d-flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={openReschedule}
              >
                Reschedule
              </Button>
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => setCancelOpen(true)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => toast.success("Reminder sent")}
              >
                Send reminder
              </Button>
            </div> */}
          </>
        )}
      </DrawerPanel>

      <Modal show={resOpen} onHide={() => setResOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Reschedule</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DatePicker
            selected={newStart}
            onChange={(d) => d && setNewStart(d)}
            showTimeSelect
            className="form-control"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={confirmReschedule}>Save</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={confirmCancel}
        title="Cancel meeting?"
        message="Attendees will be notified."
        confirmLabel="Cancel meeting"
        danger
      />
    </div>
  );
}
